import { c as _c } from "react/compiler-runtime";
import { forwardRef, useEffect, useState, type ChangeEvent, type FocusEvent } from 'react';
import { FormInput, FormInputGroup, type FormInputGroupProps, type InputProps } from './form-input';
interface NumberBehaviorProps {
  max?: number;
  min?: number;
  onBlur?: (event: FocusEvent<HTMLInputElement>, processedNumber: number) => void;
  onChange: (value: number) => void;
  toFixed?: number;
  type?: 'float' | 'integer';
  value: number;
}
interface FormInputNumberProps extends Omit<InputProps, keyof NumberBehaviorProps | 'max' | 'min'>, NumberBehaviorProps {}
interface FormInputNumberGroupProps extends Omit<FormInputGroupProps, keyof NumberBehaviorProps | 'max' | 'min'>, NumberBehaviorProps {}
type NumberInputProps = FormInputNumberProps | FormInputNumberGroupProps;
const normalizeNumberInput = (inputValue: string, {
  max,
  min,
  type = 'float'
}: Pick<NumberBehaviorProps, 'max' | 'min' | 'type'>) => {
  let value = (type === 'integer' ? Number.parseInt(inputValue, 10) : Number.parseFloat(inputValue)) || 0;
  if (max !== undefined) value = Math.min(value, max);
  if (min !== undefined) value = Math.max(value, min);
  if (type === 'integer') value = Math.max(0, value);
  return value;
};
const FormInputNumberBase = forwardRef<HTMLInputElement, NumberInputProps & {
  grouped?: boolean;
}>(function FormInputNumberBase(t0, ref) {
  const $ = _c(35);
  let grouped;
  let inputProps;
  let max;
  let min;
  let onBlur;
  let onChange;
  let t1;
  let t2;
  let value;
  if ($[0] !== t0) {
    ({
      grouped,
      max,
      min,
      onBlur,
      onChange,
      toFixed: t1,
      type: t2,
      value,
      ...inputProps
    } = t0);
    $[0] = t0;
    $[1] = grouped;
    $[2] = inputProps;
    $[3] = max;
    $[4] = min;
    $[5] = onBlur;
    $[6] = onChange;
    $[7] = t1;
    $[8] = t2;
    $[9] = value;
  } else {
    grouped = $[1];
    inputProps = $[2];
    max = $[3];
    min = $[4];
    onBlur = $[5];
    onChange = $[6];
    t1 = $[7];
    t2 = $[8];
    value = $[9];
  }
  const toFixed = t1 === undefined ? 2 : t1;
  const type = t2 === undefined ? "float" : t2;
  let t3;
  if ($[10] !== value) {
    t3 = value.toString();
    $[10] = value;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const [temporaryValue, setTemporaryValue] = useState(t3);
  let t4;
  let t5;
  if ($[12] !== value) {
    t4 = () => {
      setTemporaryValue(value.toString());
    };
    t5 = [value];
    $[12] = value;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  if ($[15] !== toFixed || $[16] !== type) {
    t6 = event => {
      const inputValue = event.target.value;
      if (type === "integer") {
        if (/^[0-9]*$/.test(inputValue)) {
          setTemporaryValue(inputValue);
        }
        return;
      }
      const normalizedValue = inputValue.replace(",", ".");
      const decimalPattern = new RegExp(`^-?\\d*(\\.\\d{0,${toFixed}})?$`);
      if (decimalPattern.test(normalizedValue)) {
        setTemporaryValue(normalizedValue);
      }
    };
    $[15] = toFixed;
    $[16] = type;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const handleChange = t6;
  let t7;
  if ($[18] !== max || $[19] !== min || $[20] !== onBlur || $[21] !== onChange || $[22] !== temporaryValue || $[23] !== toFixed || $[24] !== type) {
    t7 = event_0 => {
      const processedNumber = normalizeNumberInput(temporaryValue, {
        max,
        min,
        type
      });
      onChange(processedNumber);
      setTemporaryValue(type === "integer" ? processedNumber.toString() : processedNumber.toFixed(toFixed));
      onBlur?.(event_0, processedNumber);
    };
    $[18] = max;
    $[19] = min;
    $[20] = onBlur;
    $[21] = onChange;
    $[22] = temporaryValue;
    $[23] = toFixed;
    $[24] = type;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const handleBlur = t7;
  const Component = grouped ? FormInputGroup : FormInput;
  const t8 = type === "integer" ? 0 : min;
  let t9;
  if ($[26] !== Component || $[27] !== handleBlur || $[28] !== handleChange || $[29] !== inputProps || $[30] !== max || $[31] !== ref || $[32] !== t8 || $[33] !== temporaryValue) {
    t9 = <Component {...inputProps} ref={ref} max={max} min={t8} onBlur={handleBlur} onChange={handleChange} type="text" value={temporaryValue} />;
    $[26] = Component;
    $[27] = handleBlur;
    $[28] = handleChange;
    $[29] = inputProps;
    $[30] = max;
    $[31] = ref;
    $[32] = t8;
    $[33] = temporaryValue;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
});
const FormInputNumber = forwardRef<HTMLInputElement, FormInputNumberProps>((props, ref) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== props || $[1] !== ref) {
    t0 = <FormInputNumberBase {...props} ref={ref} />;
    $[0] = props;
    $[1] = ref;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
});
const FormInputNumberGroup = forwardRef<HTMLInputElement, FormInputNumberGroupProps>((props, ref) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== props || $[1] !== ref) {
    t0 = <FormInputNumberBase {...props} ref={ref} grouped={true} />;
    $[0] = props;
    $[1] = ref;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
});
FormInputNumber.displayName = 'FormInputNumber';
FormInputNumberGroup.displayName = 'FormInputNumberGroup';
export { FormInputNumber, FormInputNumberGroup, normalizeNumberInput };
export type { FormInputNumberGroupProps, FormInputNumberProps };